import { useState } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Camera, Trash2, Upload, Sparkles, Wand2, Video, ShieldCheck } from "lucide-react";
import { api } from "@/App";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useLocale } from "@/i18n/LocaleContext";

export default function PhotoGallery({ photos = [], videos = [], onPhotosUpdate }) {
  const { t } = useLocale();
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [aiDialog, setAiDialog] = useState(false);
  const [aiMode, setAiMode] = useState("generate");
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiPhotoIndex, setAiPhotoIndex] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [videoUploading, setVideoUploading] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const [deletingVideo, setDeletingVideo] = useState(null);

  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("video/")) {
      toast.error("Please select a video file");
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      toast.error("Video must be less than 50MB");
      return;
    }
    setVideoUploading(true);
    setVideoProgress(0);
    try {
      const chunkSize = 3 * 1024 * 1024;
      const totalChunks = Math.ceil(file.size / chunkSize);
      const uploadId = Array.from(crypto.getRandomValues(new Uint8Array(16)))
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");
      for (let i = 0; i < totalChunks; i++) {
        const chunk = file.slice(i * chunkSize, (i + 1) * chunkSize);
        const fd = new FormData();
        fd.append("upload_id", uploadId);
        fd.append("chunk_index", i);
        fd.append("total_chunks", totalChunks);
        fd.append("file", chunk, "chunk");
        await api.post("/upload/video/chunk", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setVideoProgress(Math.round(((i + 1) / totalChunks) * 70));
      }
      setVideoProgress(80);
      const res = await api.post("/upload/video/complete", {
        upload_id: uploadId,
        total_chunks: totalChunks,
        filename: file.name,
        content_type: file.type,
      });
      setVideoProgress(100);
      toast.success(`Video added! (${res.data.total_videos}/2)`);
      onPhotosUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload video");
    } finally {
      setVideoUploading(false);
    }
  };

  const handleDeleteVideo = async (index) => {
    setDeletingVideo(index);
    try {
      await api.delete(`/videos/${index}`);
      toast.success("Video deleted");
      onPhotosUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete video");
    } finally {
      setDeletingVideo(null);
    }
  };

  const handleAiCreate = async () => {
    if (!aiPrompt.trim()) {
      toast.error("Describe the photo you want");
      return;
    }
    if (aiMode === "enhance" && aiPhotoIndex === null) {
      toast.error("Select a photo to enhance");
      return;
    }
    setGenerating(true);
    try {
      const response = await api.post("/profile/ai-photo", {
        mode: aiMode,
        prompt: aiPrompt.trim(),
        photo_index: aiMode === "enhance" ? aiPhotoIndex : null,
      });
      toast.success(`AI photo added! (${response.data.total_photos}/6)`);
      setAiDialog(false);
      setAiPrompt("");
      setAiPhotoIndex(null);
      onPhotosUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || "AI photo creation failed");
    } finally {
      setGenerating(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error("Please select an image file");
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be less than 5MB");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/upload/gallery-photo', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      toast.success(`Photo added! (${response.data.total_photos}/6)`);
      onPhotosUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload photo");
    } finally {
      setUploading(false);
    }
  };

  const handleDeletePhoto = async (index) => {
    setDeleting(index);
    try {
      await api.delete(`/photos/${index}`);
      toast.success("Photo deleted");
      onPhotosUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete photo");
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="glass-card p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-semibold flex items-center gap-2">
            <Camera className="w-4 h-4" />
            Photo Gallery ({photos.length}/6)
          </h3>
          <p className="text-xs text-muted-foreground mt-1">
            Add up to 6 photos to showcase your personality
          </p>
        </div>
        {photos.length < 6 && (
          <Button
            size="sm"
            variant="outline"
            className="border-primary/30 text-primary hover:bg-primary/10"
            onClick={() => setAiDialog(true)}
            data-testid="ai-photo-btn"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            Create with AI
          </Button>
        )}
      </div>

      {/* Photo Grid */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        {photos.map((photo, index) => (
          <div
            key={index}
            className="relative aspect-square rounded-xl overflow-hidden group"
          >
            <img
              src={photo}
              alt={`Photo ${index + 1}`}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              <Button
                size="sm"
                variant="destructive"
                className="rounded-full"
                onClick={() => handleDeletePhoto(index)}
                disabled={deleting === index}
              >
                {deleting === index ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
              </Button>
            </div>
            {index === 0 && (
              <div className="absolute top-2 left-2 px-2 py-1 rounded-md bg-primary text-xs font-medium">
                Main
              </div>
            )}
          </div>
        ))}

        {/* Add Photo Button */}
        {photos.length < 6 && (
          <label className="aspect-square rounded-xl border-2 border-dashed border-white/20 hover:border-primary/50 transition-colors flex flex-col items-center justify-center cursor-pointer group">
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handlePhotoUpload}
              disabled={uploading}
            />
            {uploading ? (
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
            ) : (
              <>
                <Upload className="w-8 h-8 text-muted-foreground group-hover:text-primary transition-colors mb-2" />
                <span className="text-xs text-muted-foreground group-hover:text-white transition-colors">
                  {t("gal.addPhoto")}
                </span>
              </>
            )}
          </label>
        )}
      </div>

      {photos.length === 0 && (
        <p className="text-center text-sm text-muted-foreground py-4">
          {t("gal.noPhotos")}
        </p>
      )}

      {/* Video Intros */}
      <div className="mt-6 pt-5 border-t border-white/10" data-testid="video-gallery-section">
        <div className="mb-3">
          <h3 className="font-semibold flex items-center gap-2 text-sm">
            <Video className="w-4 h-4" />
            {t("gal.videoIntros", { n: videos.length })}
          </h3>
          <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5 text-green-400 shrink-0" />
            {t("gal.videoRules")}
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {videos.map((video, index) => (
            <div key={index} className="relative aspect-video rounded-xl overflow-hidden bg-black group">
              <video
                src={video}
                controls
                playsInline
                preload="metadata"
                className="w-full h-full object-cover"
                data-testid={`gallery-video-${index}`}
              />
              <button
                type="button"
                onClick={() => handleDeleteVideo(index)}
                disabled={deletingVideo === index}
                className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/70 backdrop-blur-sm flex items-center justify-center hover:bg-red-500/80 transition-colors"
                data-testid={`delete-video-${index}`}
              >
                {deletingVideo === index ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                ) : (
                  <Trash2 className="w-4 h-4 text-white" />
                )}
              </button>
            </div>
          ))}

          {videoUploading ? (
            <div className="aspect-video rounded-xl border-2 border-dashed border-primary/40 flex flex-col items-center justify-center gap-2" data-testid="video-upload-progress">
              <div className="animate-spin rounded-full h-7 w-7 border-t-2 border-b-2 border-primary"></div>
              <span className="text-xs text-muted-foreground">
                {videoProgress < 75 ? `Uploading... ${videoProgress}%` : "AI content check..."}
              </span>
            </div>
          ) : (
            videos.length < 2 && (
              <label className="aspect-video rounded-xl border-2 border-dashed border-white/20 hover:border-primary/50 transition-colors flex flex-col items-center justify-center cursor-pointer group" data-testid="add-video-label">
                <input
                  type="file"
                  accept="video/*"
                  className="hidden"
                  onChange={handleVideoUpload}
                  disabled={videoUploading}
                  data-testid="video-upload-input"
                />
                <Video className="w-7 h-7 text-muted-foreground group-hover:text-primary transition-colors mb-1.5" />
                <span className="text-xs text-muted-foreground group-hover:text-white transition-colors">
                  {t("gal.addVideo")}
                </span>
              </label>
            )
          )}
        </div>
      </div>

      {/* AI Photo Dialog */}
      <Dialog open={aiDialog} onOpenChange={setAiDialog}>
        <DialogContent data-testid="ai-photo-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wand2 className="h-5 w-5 text-primary" />
              Create a Photo with AI
            </DialogTitle>
            <DialogDescription>
              Generate a brand-new photo or enhance one you've already uploaded
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                className={`px-3 py-2.5 rounded-xl text-sm border transition-colors ${
                  aiMode === "generate"
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-white/10 bg-white/5 text-muted-foreground hover:bg-white/10"
                }`}
                onClick={() => setAiMode("generate")}
                data-testid="ai-mode-generate"
              >
                ✨ Generate new
              </button>
              <button
                type="button"
                className={`px-3 py-2.5 rounded-xl text-sm border transition-colors ${
                  aiMode === "enhance"
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-white/10 bg-white/5 text-muted-foreground hover:bg-white/10"
                }`}
                onClick={() => setAiMode("enhance")}
                disabled={photos.length === 0}
                data-testid="ai-mode-enhance"
              >
                🪄 Enhance my photo
              </button>
            </div>

            {aiMode === "enhance" && (
              <div>
                <p className="text-xs text-muted-foreground mb-2">Pick the photo to enhance:</p>
                <div className="grid grid-cols-6 gap-2">
                  {photos.map((photo, index) => (
                    <button
                      key={index}
                      type="button"
                      className={`aspect-square rounded-lg overflow-hidden border-2 transition-colors ${
                        aiPhotoIndex === index ? "border-primary" : "border-transparent opacity-70 hover:opacity-100"
                      }`}
                      onClick={() => setAiPhotoIndex(index)}
                      data-testid={`ai-source-photo-${index}`}
                    >
                      <img src={photo} alt={`Photo ${index + 1}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              </div>
            )}

            <Textarea
              placeholder={
                aiMode === "enhance"
                  ? 'e.g. "brighten the lighting and blur the background" or "make it look like a professional studio portrait"'
                  : 'e.g. "a stylish portrait of me in a tailored suit at a rooftop bar at sunset"'
              }
              className="bg-white/5 border-white/10 min-h-[90px]"
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              data-testid="ai-prompt-input"
            />

            <p className="text-xs text-muted-foreground">
              Photos should truthfully represent you. AI images that misrepresent your appearance may be removed.
            </p>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setAiDialog(false)} data-testid="ai-photo-cancel-btn">
              Cancel
            </Button>
            <Button
              onClick={handleAiCreate}
              disabled={generating}
              className="bg-primary hover:bg-primary/90"
              data-testid="ai-photo-create-btn"
            >
              {generating ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                  Creating... (~15s)
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Create Photo
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
